import { useCallback, useEffect, useState } from 'react';

import { Box, Button, Heading, Text, VStack } from '@chakra-ui/react';

import { getDD2DealsUrl } from '../config';

const CONFETTI_DURATION_MS = 750;

const CONFETTI_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'] as const;

type ConfettiParticle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  rotation: number;
  rotationSpeed: number;
  shape: 'rect' | 'circle';
};

const fireConfetti = (): void => {
  const canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  Object.assign(canvas.style, {
    position: 'fixed',
    inset: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: '9999',
  });
  document.body.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  if (ctx == null) {
    canvas.remove();
    return;
  }

  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  const originX = canvas.width / 2;
  const originY = canvas.height * 0.55;

  const particles: ConfettiParticle[] = Array.from({ length: 120 }, () => {
    const angle = Math.random() * Math.PI * 2;
    const speed = Math.random() * 10 + 6;

    return {
      x: originX,
      y: originY,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 6,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: Math.random() * 6 + 4,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 12,
      shape: Math.random() > 0.5 ? 'rect' : 'circle',
    };
  });

  const gravity = 0.25;
  const startTime = Date.now();
  let animationId = 0;

  const animate = (): void => {
    const elapsed = Date.now() - startTime;
    if (elapsed > CONFETTI_DURATION_MS) {
      cancelAnimationFrame(animationId);
      canvas.remove();
      return;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let i = 0; i < particles.length; i += 1) {
      const currentParticle = particles[i];
      currentParticle.vy += gravity;
      currentParticle.x += currentParticle.vx;
      currentParticle.y += currentParticle.vy;
      currentParticle.rotation += currentParticle.rotationSpeed;
      currentParticle.vx *= 0.99;

      ctx.save();
      ctx.translate(currentParticle.x, currentParticle.y);
      ctx.rotate((currentParticle.rotation * Math.PI) / 180);
      ctx.fillStyle = currentParticle.color;
      ctx.globalAlpha = 1 - elapsed / CONFETTI_DURATION_MS;

      if (currentParticle.shape === 'rect') {
        ctx.fillRect(
          -currentParticle.size / 2,
          -currentParticle.size / 2,
          currentParticle.size,
          currentParticle.size * 0.6,
        );
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, currentParticle.size / 2, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    }

    animationId = requestAnimationFrame(animate);
  };

  animationId = requestAnimationFrame(animate);
};

const DD2Styles = {
  background: 'oklch(1 0 0)',
  foreground: 'oklch(0.3294 0.0945 254.3608)',
  mutedForeground: 'oklch(0.5544 0.0407 257.4166)',
  border: 'oklch(0.9288 0.0126 255.5078)',
  primary: 'oklch(0.5575 0.1528 248.7413)',
} as const;

const DD2_FONT_FAMILY = "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const POPPINS_STYLESHEET_ID = 'dd2-poppins-font';

const DirectToDD2 = () => {
  const [isNavigating, setIsNavigating] = useState(false);

  const handleGoToDD2 = useCallback(() => {
    if (isNavigating) {
      return;
    }

    setIsNavigating(true);
    fireConfetti();

    window.setTimeout(() => {
      window.location.assign(getDD2DealsUrl());
    }, CONFETTI_DURATION_MS);
  }, [isNavigating]);

  useEffect(() => {
    if (document.getElementById(POPPINS_STYLESHEET_ID) != null) {
      return;
    }

    const link = document.createElement('link');
    link.id = POPPINS_STYLESHEET_ID;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap';
    document.head.appendChild(link);
  }, []);

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg={DD2Styles.background}
      fontFamily={DD2_FONT_FAMILY}
      px={4}
    >
      <Box
        w="full"
        maxW="32rem"
        p={6}
        borderWidth="1px"
        borderColor={DD2Styles.border}
        borderRadius="12px"
        bg={DD2Styles.background}
        boxShadow="lg"
      >
        <VStack spacing={4} textAlign="center">
          <Heading
            as="h1"
            fontSize="20px"
            fontWeight={600}
            lineHeight="1"
            color={DD2Styles.foreground}
          >
            DD 2.0 is here!
          </Heading>

          <Text fontSize="14px" lineHeight="1.5" color={DD2Styles.mutedForeground}>
            You have a cool new app to use, why would you go back to the old one?
          </Text>

          <Button
            h="40px"
            px={6}
            borderRadius="12px"
            fontWeight={500}
            fontSize="14px"
            textTransform="none"
            letterSpacing="normal"
            margin={0}
            bg={DD2Styles.primary}
            color="white"
            boxShadow="sm"
            isLoading={isNavigating}
            loadingText="Launching..."
            aria-label="Go to DD 2.0"
            onClick={handleGoToDD2}
            _hover={{ bg: DD2Styles.primary, opacity: 0.9 }}
            _active={{ bg: DD2Styles.primary, opacity: 0.9 }}
          >
            Go to 2.0
          </Button>
        </VStack>
      </Box>
    </Box>
  );
};

export default DirectToDD2;
